/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid engine/validate.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

// v9 exposes an API only when its feature is registered - a config flag whose
// feature is missing from a custom `features` set would otherwise surface as
// an undefined-method crash deep in a render. This maps each flag to the
// registration keys its code paths reach and fails fast at construction with
// the flag/feature pair spelled out.
export interface FeatureCoverageFlags {
  cellSelection: boolean
  columnFilters: boolean
  columnOrder: boolean
  columnPinning: boolean
  columnSizing: boolean
  columnVisibility: boolean
  globalFilter: boolean
  pagination: boolean
  rowSelection: boolean
  // columnFilters with a filterType: 'select' column - the value list comes
  // from faceting.
  selectFilters: boolean
  sorting: boolean
}

const FLAG_FEATURES: ReadonlyArray<[keyof FeatureCoverageFlags, readonly string[]]> = [
  ['cellSelection', ['cellSelectionFeature']],
  ['columnFilters', ['columnFilteringFeature', 'filteredRowModel']],
  ['columnOrder', ['columnOrderingFeature']],
  // Pinning offsets come from column.getSize() and the edge classes from
  // getIsVisible(), so pinning drags sizing + visibility in with it.
  ['columnPinning', ['columnPinningFeature', 'columnSizingFeature', 'columnVisibilityFeature']],
  ['columnSizing', ['columnResizingFeature', 'columnSizingFeature']],
  ['columnVisibility', ['columnVisibilityFeature']],
  ['globalFilter', ['filteredRowModel', 'filterFns', 'globalFilteringFeature']],
  ['pagination', ['paginatedRowModel', 'rowPaginationFeature']],
  ['rowSelection', ['rowSelectionFeature']],
  ['selectFilters', ['columnFacetingFeature', 'facetedRowModel', 'facetedUniqueValues']],
  ['sorting', ['rowSortingFeature', 'sortedRowModel']]
]

export const assertFeatureCoverage = (
  componentName: string,
  features: object,
  flags: FeatureCoverageFlags
): void => {
  for (const [flag, requiredKeys] of FLAG_FEATURES) {
    if (!flags[flag]) {
      continue
    }

    const label = flag === 'selectFilters' ? 'columnFilters (filterType "select")' : flag
    for (const key of requiredKeys) {
      if (!(key in features)) {
        throw new TypeError(
          `${componentName.toUpperCase()}: Option "${label}" requires feature "${key}" in "features".`
        )
      }
    }
  }
}
